/**
 * Benachrichtigungen einrichten.
 *
 * Die iOS-Regeln sind hier eingebaut, nicht nur dokumentiert:
 *
 * - **Berechtigung nur nach echtem Fingertipp.** `requestPermission()` beim Laden
 *   der Seite lehnt iOS stillschweigend ab — und ein zweites Mal fragen darf man
 *   danach nicht mehr.
 * - **Nur als installierte PWA.** Im Safari-Tab gibt es kein Push. Deshalb wird
 *   das erkannt und erklärt, statt einen Knopf anzubieten, der nichts tut.
 * - **Abo bei jedem Start erneuern.** iOS kündigt Abos ohne Vorwarnung; wer sich
 *   auf das einmal gespeicherte verlässt, hört irgendwann einfach nichts mehr.
 */

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../api.js';

export type PushStatus =
  | 'unsupported'
  | 'needs-install'
  | 'not-configured'
  | 'denied'
  | 'idle'
  | 'enabling'
  | 'enabled'
  | 'error';

export interface PushState {
  status: PushStatus;
  error: string | null;
  enable: () => void;
}

/** Läuft die App vom Home-Bildschirm? Auf iOS Voraussetzung für Push. */
export function isStandalone(): boolean {
  const iosStandalone = (navigator as { standalone?: boolean }).standalone === true;
  // Ohne `matchMedia` (alte Browser, Testumgebung) zählt nur das iOS-Kennzeichen.
  return (
    iosStandalone ||
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches)
  );
}

function isApplePlatform(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

/** VAPID-Schlüssel kommen base64url — `applicationServerKey` will Bytes. */
export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const normalized = padded.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  // Ausdrücklich über einen eigenen ArrayBuffer: `applicationServerKey` verlangt
  // eine BufferSource, und ein Uint8Array über einem geteilten Puffer ist keine.
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

async function sendSubscriptionToServer(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON() as { endpoint?: string; keys?: Record<string, string> };
  await apiFetch('/push/subscribe', {
    method: 'POST',
    body: JSON.stringify({
      endpoint: json.endpoint,
      keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
    }),
  });
}

function initialStatus(): PushStatus {
  if (
    !('serviceWorker' in navigator) ||
    !('PushManager' in window) ||
    !('Notification' in window)
  ) {
    return 'unsupported';
  }
  // Auf iPhone/iPad ohne Installation gibt es keinen Weg — das ist keine
  // Einstellung, sondern eine Plattformregel.
  if (isApplePlatform() && !isStandalone()) return 'needs-install';
  if (Notification.permission === 'denied') return 'denied';
  return 'idle';
}

export function usePush(enabled: boolean): PushState {
  const [status, setStatus] = useState<PushStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  // Beim Start: Zustand feststellen und ein vorhandenes Abo erneuern.
  useEffect(() => {
    if (!enabled) return;

    const start = initialStatus();
    setStatus(start);
    if (start !== 'idle') return;

    void (async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        const existing = await registration.pushManager.getSubscription();
        if (!existing) return;

        // Erneuern statt vertrauen: iOS kündigt Abos von sich aus.
        await sendSubscriptionToServer(existing);
        setStatus('enabled');
      } catch {
        // Kein Grund für eine Fehlermeldung — der Nutzer kann jederzeit tippen.
      }
    })();
  }, [enabled]);

  const enable = useCallback(() => {
    void (async () => {
      setError(null);
      setStatus('enabling');

      try {
        const { publicKey } = await apiFetch<{ publicKey: string }>('/push/public-key');
        if (!publicKey) {
          setStatus('not-configured');
          return;
        }

        // Direkt im Tap-Kontext — dazwischen darf nichts Langes liegen.
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          setStatus(permission === 'denied' ? 'denied' : 'idle');
          return;
        }

        const registration = await navigator.serviceWorker.ready;
        const subscription =
          (await registration.pushManager.getSubscription()) ??
          (await registration.pushManager.subscribe({
            // Pflicht: jeder Push muss sichtbar werden. Stille Push-Nachrichten
            // lässt iOS nicht zu und kündigt bei Verstoß das Abo.
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey),
          }));

        await sendSubscriptionToServer(subscription);
        setStatus('enabled');
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Unbekannter Fehler');
        setStatus('error');
      }
    })();
  }, []);

  return { status, error, enable };
}
